import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, filter, finalize, map, of, take, tap, throwError, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { userHasPermission as checkTreePermission } from './permissions';
import { SessionExpiredService } from './session-expired.service';
import { GoogleIdentityService } from './google-identity.service';

/** Valore di `dataAccessLevel` che concede l'accesso globale ai dati. */
export const GLOBAL_DATA_ACCESS_LEVEL = 'global';

export interface UserDcr {
  id: string;
  Name: string;
  name?: string;
  hasVisionAccess?: boolean;
  isSandbox?: boolean;
  firstPartyGroupID?: string | null;
}

export interface SessionUser {
  ID: string;
  email: string;
  entityID: string | null;
  status: number;
  availableDCRs: UserDcr[];
  dataAccessLevel?: string;
  name?: string;
  permissions: string[];
  token?: string;
  tokenExpiry?: number;
  expires?: number;
  tncApiToken: string | null;
  firstPartyGroupID?: string | null;
  /**
   * Experimental opt-ins, `{ featureId: true }`. Server-owned and delivered with the session like
   * `permissions` — never written here directly: ExperimentalFeaturesService owns the toggle.
   */
  experimentalFeatures?: Record<string, boolean>;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ApiResponse<T> {
  payload?: T;
  error?: {
    message: string;
    status: number;
    code: string;
  };
  dataUpdateDate?: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private readonly TNCDataEntityId = environment.TNCDataEntityId;
  private currentUserSubject = new BehaviorSubject<SessionUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private selectedDcrSubject = new BehaviorSubject<string | null>(null);
  public selectedDcr$ = this.selectedDcrSubject.asObservable();
  private entitySyncPendingSubject = new BehaviorSubject<boolean>(false);
  readonly entitySyncPending$ = this.entitySyncPendingSubject.asObservable();

  private readonly IMPERSONATE_KEY = 'admin_original_session';
  private refreshingPrivileges = false;

  get isImpersonating(): boolean {
    return !!localStorage.getItem(this.IMPERSONATE_KEY);
  }

  get realUserHasGlobalDataAccess(): boolean {
    const saved = localStorage.getItem(this.IMPERSONATE_KEY);
    if (saved) {
      try {
        const { userData } = JSON.parse(saved);
        return userData?.dataAccessLevel === GLOBAL_DATA_ACCESS_LEVEL;
      } catch {}
    }
    return this.hasGlobalDataAccess();
  }

  getRealUserAuthHeaders(): { [key: string]: string } {
    const saved = localStorage.getItem(this.IMPERSONATE_KEY);
    if (saved) {
      try {
        const { token } = JSON.parse(saved);
        if (token) {
          return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        }
      } catch {}
    }
    return this.getAuthHeaders();
  }

  startImpersonation(impersonatedUser: SessionUser): void {
    if (!this.isImpersonating) {
      localStorage.setItem(this.IMPERSONATE_KEY, JSON.stringify({
        token: this.getStoredToken(),
        userData: this.getStoredUserData(),
        availableDCRs: this.currentUserValue?.availableDCRs ?? []
      }));
    }

    const normalized = this.normalizeUserResponse(impersonatedUser);

    // When DCRs are available, normalizeUserResponse already validated entityID against the list.
    // When the list is empty (user not in active_dcr_compass), use null instead of a raw UUID
    // that can't be resolved to a display name in the entity selector.
    const availableDCRs = normalized.availableDCRs ?? [];
    const entityId = availableDCRs.length > 0 ? (normalized.entityID ?? availableDCRs[0].id) : null;
    normalized.entityID = entityId;

    this.storeAuthData(normalized.token!, normalized);
    // selectedDcrSubject va impostato PRIMA di currentUserSubject: il re-render
    // scatenato da currentUserSubject legge selectedDcrSubject in modo sincrono
    this.selectedDcrSubject.next(entityId);
    this.currentUserSubject.next(normalized);
  }

  stopImpersonation(): void {
    const saved = localStorage.getItem(this.IMPERSONATE_KEY);
    if (!saved) return;
    try {
      const entityFromImpersonation = this.currentUserSubject.value?.entityID ?? null;
      const { token, userData, availableDCRs } = JSON.parse(saved);
      localStorage.removeItem(this.IMPERSONATE_KEY);
      this.storeAuthData(token, userData);
      const normalized = this.normalizeUserResponse({ ...userData, token });
      if (availableDCRs?.length > 0) {
        normalized.availableDCRs = availableDCRs;
      }
      const entityToSelect = entityFromImpersonation ?? normalized.entityID;
      normalized.entityID = entityToSelect;
      this.currentUserSubject.next(normalized);
      this.selectedDcrSubject.next(entityToSelect);
      this.changeSelectedEntity(entityToSelect).subscribe();
    } catch (e) {
      console.error('[auth] stopImpersonation failed:', e);
    }
  }

  hasGlobalDataScope(): boolean {
    return this.hasGlobalDataAccess() && !this.getActiveDcrId();
  }

  waitForEntitySync(): Observable<void> {
    return this.entitySyncPending$.pipe(
      filter(pending => !pending),
      take(1),
      map(() => undefined)
    );
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private sessionExpiredService: SessionExpiredService,
    private googleIdentity: GoogleIdentityService
  ) {
    // Check for existing token on service initialization
    this.loadStoredUser();
  }

  /**
   * A stored session that predates the current contract has not expired, but lacks
   * `dataAccessLevel` — the field every access decision derives from — so a user with global data
   * access would silently be downgraded to a regular one. Force a fresh login instead.
   *
   * Checked as a *whitelist* (the required field must be there) rather than by blacklisting the old
   * `isAdmin`: blacklisting let through the in-between generation, which had `hasGlobalDataAccess`
   * but not yet `dataAccessLevel`, and those were declassed silently — the exact failure this guard
   * exists to prevent. A missing ID is treated the same: it means the stored payload is degraded.
   */
  private isStaleSessionPayload(storedUser: unknown): boolean {
    if (!storedUser || typeof storedUser !== 'object') {
      return false; // nothing stored: not a stale session, just an anonymous visitor
    }
    const data = storedUser as { dataAccessLevel?: unknown; ID?: unknown };
    const levelMissing = typeof data.dataAccessLevel !== 'string' || data.dataAccessLevel.trim() === '';
    const idMissing = typeof data.ID !== 'string' || data.ID.trim() === '' || data.ID === 'undefined';
    return levelMissing || idMissing;
  }

  /**
   * Load user from localStorage if available
   */
  private loadStoredUser(): void {
    const token = this.getStoredToken();
    const storedUser = this.getStoredUserData();

    // Drop pre-contract sessions instead of letting them run with a silently missing data scope.
    if (this.isStaleSessionPayload(storedUser)) {
      console.warn('[auth] loadStoredUser - stale session payload (no dataAccessLevel), forcing re-login');
      this.clearAuthData();
      this.sessionExpiredService.show('updated');
      return;
    }

    if (token && storedUser) {
      const userData = this.normalizeUserResponse(storedUser);

      const storedApiToken = (storedUser as SessionUser).tncApiToken ?? this.getStoredTncApiToken();
      if (storedApiToken && !userData.tncApiToken) {
        userData.tncApiToken = storedApiToken;
      }
      // Check if token is expired
      if (userData.expires && Date.now() > userData.expires) {
        console.warn('[auth] loadStoredUser - Token expired, logging out. Expires:', userData.expires, 'Now:', Date.now());
        this.logout();
        return;
      }

      this.storeTncApiToken(userData.tncApiToken);

      // Re-sync the server-side JWT with the stored entity before any data consumers fire.
      // We set entitySyncPending$=true synchronously here so that services using
      // waitForEntitySync() will wait; the HTTP call itself is deferred outside the
      // constructor cycle to avoid a circular dependency with HTTP_INTERCEPTORS.
      // Global Data Scope (entityID null on a global-access user) must be re-synced too:
      // the server-side JWT would otherwise keep whatever entity was last selected.
      const isGlobalScope =
        !userData.entityID && userData.dataAccessLevel === GLOBAL_DATA_ACCESS_LEVEL;
      if (userData.entityID || isGlobalScope) {
        this.entitySyncPendingSubject.next(true);
        const entityId = userData.entityID;
        setTimeout(() => this.changeSelectedEntity(entityId).subscribe());
      }

      this.currentUserSubject.next(userData);
      this.selectedDcrSubject.next(userData.entityID);
    }
  }

  /**
   * Login with email and password - usando formato API originale
   */
  login(credentials: LoginRequest): Observable<SessionUser> {
    // Facciamo una sola chiamata a /webapp/login che ora restituisce già tutti i dati necessari
    return this.http.post<any>(`${this.getAuthApiUrl()}/login`, credentials, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(rawResponse => {
        const responseData = rawResponse?.payload || rawResponse;
        const userData = this.normalizeUserResponse(responseData);

        if (!userData.token) {
          throw new Error('Missing authentication token in login response');
        }

        if (userData.tokenExpiry && !userData.expires) {
          userData.expires = userData.tokenExpiry * 1000;
        }

        this.storeAuthData(userData.token, userData);
        this.currentUserSubject.next(userData);
        this.selectedDcrSubject.next(userData.entityID);

        return userData;
      }),
      catchError(error => {
        console.error('Login error:', error);
        throw new Error(this.resolveLoginError(error, {
          409: 'login.errors.pendingApproval'
        }, 'login.errors.invalidCredentials'));
      })
    );
  }

  /**
   * Sign in with a Google account.
   *
   * `credential` is the ID token Google Identity Services hands the browser. Everything past the
   * HTTP call is identical to `login()` on purpose — the session it stores is the same one, only
   * the proof of identity differs — so this must keep going through the same normalize/store path
   * rather than growing its own.
   */
  loginWithGoogle(credential: string): Observable<SessionUser> {
    return this.http.post<any>(`${this.getAuthApiUrl()}/google-login`, { credential }, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(rawResponse => {
        const responseData = rawResponse?.payload || rawResponse;
        const userData = this.normalizeUserResponse(responseData);

        if (!userData.token) {
          throw new Error('Missing authentication token in login response');
        }

        if (userData.tokenExpiry && !userData.expires) {
          userData.expires = userData.tokenExpiry * 1000;
        }

        this.storeAuthData(userData.token, userData);
        this.currentUserSubject.next(userData);
        this.selectedDcrSubject.next(userData.entityID);

        return userData;
      }),
      catchError(error => {
        console.error('Google login error:', error);
        // Status-driven: see resolveLoginError for why the body's wording isn't used here.
        throw new Error(this.resolveLoginError(error, {
          400: 'login.errors.googleNotAuthorized',
          401: 'login.errors.googleInvalidCredential',
          403: 'login.errors.googleUserInactive',
          409: 'login.errors.googlePendingApproval'
        }, 'login.errors.googleSignInFailed'));
      })
    );
  }

  /**
   * Turn an HTTP failure from the login endpoints into something the login page can show.
   *
   * Two things went wrong without this. The API's error envelope is `{ error: "<message>" }` — a
   * bare string, not `{ error: { message } }` — so the old `error.error.error.message` lookup never
   * matched and we fell back to Angular's own `HttpErrorResponse.message`, i.e. "Http failure
   * response for .../google-login: 400 OK". And the API's wording is deliberately vague ("Invalid
   * user"), because an unauthenticated caller must not learn whether an address belongs to one of
   * our users.
   *
   * So the *status*, not the body, decides what the user reads. For Google sign-in being specific
   * is safe: the visitor has just proved to Google that they own this mailbox, so telling them it
   * has no access here reveals nothing they couldn't already find out — while a generic failure
   * sends them hunting for a problem that isn't theirs. The API keeps its vague wording for anyone
   * calling it directly; only the UI is specific.
   *
   * Returns a translation key for the cases we recognise. ngx-translate passes an unknown string
   * through unchanged, so a raw API message still reaches the user instead of being swallowed.
   */
  private resolveLoginError(
    error: any,
    keysByStatus: Record<number, string>,
    fallbackKey: string
  ): string {
    const mapped = keysByStatus[error?.status];
    if (mapped) {
      return mapped;
    }

    const body = error?.error;
    const backendMessage =
      typeof body === 'string' ? body
        : typeof body?.error === 'string' ? body.error
          : typeof body?.error?.message === 'string' ? body.error.message
            : typeof body?.message === 'string' ? body.message
              : '';

    return backendMessage || fallbackKey;
  }

  /**
   * Logout user
   */
  logout(): Observable<any> {
    const token = this.getStoredToken();

    // Drop Google's "remember this account" hint too, or the next visitor to this browser gets
    // signed straight back in as the previous one via the Google button.
    this.googleIdentity.disableAutoSelect();

    // Always clear local data first
    this.clearAuthData();
    this.currentUserSubject.next(null);
    this.selectedDcrSubject.next(null);

    if (!token) {
      return of(null);
    }

    // Call logout API to invalidate token on server with timeout
    return this.http.post<ApiResponse<any>>(`${this.getAuthApiUrl()}/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      timeout(5000), // 5 second timeout
      catchError(error => {
        console.error('Logout API error:', error);
        // Return success anyway since local data is cleared
        return of(null);
      })
    );
  }

  /**
   * "Flush privileges": ottiene dal backend un JWT aggiornato con i permessi
   * correnti del DB (senza password) e ricarica la pagina col nuovo token.
   * Innescato dall'evento SSE `privileges.flush`. Sessione revocata → logout.
   * v1: non gestisce l'impersonation (rinnova l'identità del token corrente).
   */
  refreshPrivileges(): void {
    if (this.refreshingPrivileges) return; // evita reload multipli su eventi ripetuti
    const token = this.getStoredToken();
    if (!token) return;
    this.refreshingPrivileges = true;

    this.http.post<ApiResponse<SessionUser>>(`${this.getAuthApiUrl()}/auth/refresh-jwt`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(timeout(8000)).subscribe({
      next: (response) => {
        const payload = response?.payload;
        if (response?.error || !payload || !payload.token) {
          this.refreshingPrivileges = false;
          this.logout().subscribe();
          return;
        }
        const normalized = this.normalizeUserResponse(payload);
        this.storeAuthData(normalized.token!, normalized);
        // Reload leggero: guard, menu e componenti ripartono col nuovo JWT.
        window.location.reload();
      },
      error: () => {
        this.refreshingPrivileges = false;
        this.logout().subscribe();
      }
    });
  }

  changePassword(
    oldPassword: string,
    newPassword: string
  ): Observable<void> {
    const token = this.getStoredToken();
    if (!token) {
      return throwError(() => new Error('changePassword.errors.unauthenticated'));
    }

    const endpoint = `${this.getAuthApiUrl()}/change-password`;
    // Entrambe le password viaggiano cifrate RSA e codificate base64.
    return this.http.post<ApiResponse<any>>(endpoint, {
      oldPassword,
      newPassword
    }, {
      headers: {
        ...this.getAuthHeaders()
      }
    }).pipe(
      map(response => {
        if (response?.error) {
          throw new Error(response.error.message || 'changePassword.errors.generic');
        }
        // Vecchia password errata: il backend risponde 200 con Result = 'Not Done'.
        if (response?.payload?.Result === 'Not Done') {
          throw new Error('changePassword.errors.wrongOldPassword');
        }
      }),
      catchError(error => {
        const message =
          error?.error?.error?.message ||
          error?.error?.message ||
          error?.message ||
          'changePassword.errors.generic';
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Get current user data
   */
  getCurrentUser(): Observable<SessionUser> {
    const token = this.getStoredToken();

    if (!token) {
      throw new Error('No authentication token');
    }

    return this.http.get<ApiResponse<SessionUser>>(`${this.getAuthApiUrl()}/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message);
        }

        if (!response.payload) {
          throw new Error('Invalid response format');
        }

        const userData = this.normalizeUserResponse(response.payload);
        if (!userData.token) {
          const storedToken = this.getStoredToken();
          if (storedToken) {
            userData.token = storedToken;
          }
        }
        if (userData.tokenExpiry && !userData.expires) {
          userData.expires = userData.tokenExpiry * 1000;
        }

        this.currentUserSubject.next(userData);
        this.selectedDcrSubject.next(userData.entityID);
        this.updateStoredUserData(userData);
        return userData;
      }),
      catchError(error => {
        console.error('Get current user error:', error);
        this.logout();
        throw error;
      })
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    const userData = this.getStoredUserData();

    if (!token || !userData) {
      this.logout();
      return false;
    }

    // Check if token is expired
    if (userData.expires) {
      if (Date.now() > userData.expires) {
        this.logout();
        return false;
      }
    }

    return true;
  }

  /**
   * Get current user value
   */
  get currentUserValue(): SessionUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.currentUserValue;
    return this.userHasPermission(user, permission);
  }

  /**
   * Check if user is admin
   */
  hasGlobalDataAccess(): boolean {
    const user = this.currentUserValue;
    return user?.dataAccessLevel === GLOBAL_DATA_ACCESS_LEVEL;
  }

  /**
   * Update the active DCR for the current user
   */
  setActiveDcr(dcrId: string): void {
    const user = this.currentUserSubject.value;
    if (!user) {
      return;
    }

    const normalizedDcrId = (dcrId ?? '').toString().trim();
    if (!normalizedDcrId || user.entityID === normalizedDcrId) {
      return;
    }

    const availableDcrs = user.availableDCRs || [];
    const matchedEntry = availableDcrs.find(entry => {
      if (!entry) return false;
      return entry.id === normalizedDcrId;
    });
    const dcrAllowed = user.dataAccessLevel === GLOBAL_DATA_ACCESS_LEVEL || availableDcrs.length === 0 || !!matchedEntry;

    if (!dcrAllowed) {
      return;
    }

    const resolvedDcrId = matchedEntry?.id ?? normalizedDcrId;
    const updatedUser: SessionUser = { ...user, entityID: resolvedDcrId };

    this.currentUserSubject.next(updatedUser);
    this.updateStoredUserData(updatedUser);

    this.changeSelectedEntity(resolvedDcrId).subscribe({
      next: () => this.selectedDcrSubject.next(resolvedDcrId),
      error: () => this.selectedDcrSubject.next(resolvedDcrId)
    });
  }

  setGlobalScope(): void {
    const user = this.currentUserSubject.value;
    if (user?.dataAccessLevel !== GLOBAL_DATA_ACCESS_LEVEL) return;

    const updatedUser: SessionUser = { ...user, entityID: null };
    this.currentUserSubject.next(updatedUser);
    this.updateStoredUserData(updatedUser);

    this.changeSelectedEntity(null).subscribe({
      next: () => this.selectedDcrSubject.next(null),
      error: () => this.selectedDcrSubject.next(null)
    });
  }

  /**
   * Notify backend of the selected entity change
   */
  changeSelectedEntity(entityId: string | null): Observable<any> {
    this.entitySyncPendingSubject.next(true);
    const baseUrl = environment.apiUrl.replace(/\/+$/u, '');

    // Only EntityID is sent: the endpoint derives the scope from the JWT's dataAccessLevel /
    // hasGlobalDataAccess, so an EntityType in the body would be ignored — and a second copy
    // of the same fact that can drift from the signed one.
    return this.http.post<ApiResponse<SessionUser>>(
      `${baseUrl}/webapp/change-selected-entity`,
      { EntityID: entityId },
      { headers: this.getAuthHeaders(), withCredentials: true }
    ).pipe(
      tap(response => {
        const payload = (response?.payload ?? response) as SessionUser | null | undefined;
        if (!payload) return;
        const currentUser = this.currentUserValue;
        // Prefer fresh DCRs from the response body; fall back to in-memory list
        // (e.g. Reach publishers merged via mergeAvailableDcrs).
        const freshDCRs = (payload.availableDCRs?.length ?? 0) > 0 ? payload.availableDCRs : null;
        const currentAvailable = currentUser?.availableDCRs;
        const normalized = this.normalizeUserResponse({
          ...payload,
          availableDCRs: freshDCRs ?? currentAvailable ?? payload.availableDCRs
        });
        normalized.entityID = entityId;
        // change-selected-entity does not alter admin/permission state — always preserve from current session
        if (currentUser?.dataAccessLevel) normalized.dataAccessLevel = currentUser.dataAccessLevel;
        if ((currentUser?.permissions?.length ?? 0) > 0) normalized.permissions = currentUser!.permissions;
        if (normalized.token) {
          this.storeAuthData(normalized.token, normalized);
        } else {
          const existingToken = this.getStoredToken();
          if (existingToken) normalized.token = existingToken;
          this.updateStoredUserData(normalized);
        }
        this.currentUserSubject.next(normalized);
      }),
      catchError(error => {
        console.error('[auth] change-selected-entity failed:', error);
        return of(null);
      }),
      finalize(() => {
        this.entitySyncPendingSubject.next(false);
      })
    );
  }

  /**
   * Replace the session's experimental opt-ins with a state the server has confirmed.
   * Persisted to `user_data` so an F5 doesn't flash the standard UI before the session reloads;
   * cleared with the rest of the session on logout, and replaced wholesale on impersonation.
   *
   * Only ExperimentalFeaturesService should call this — it owns the write path.
   */
  applyExperimentalFeatures(state: Record<string, boolean>): void {
    const user = this.currentUserSubject.value;
    if (!user) {
      return;
    }
    const updatedUser: SessionUser = { ...user, experimentalFeatures: { ...state } };
    this.currentUserSubject.next(updatedUser);
    this.updateStoredUserData(updatedUser);
  }

  /**
   * Merge external DCRs (e.g. Reach publishers) into the current user's available list.
   * Updates in-memory only — not persisted to localStorage.
   */
  mergeAvailableDcrs(externalDcrs: UserDcr[]): void {
    const user = this.currentUserSubject.value;
    if (!user || !Array.isArray(externalDcrs) || externalDcrs.length === 0) {
      return;
    }
    const merged = this.mergeDcrLists(user.availableDCRs ?? [], externalDcrs);
    this.currentUserSubject.next({ ...user, availableDCRs: merged });
  }

  /**
   * Check if the global data entity ("_TNC Owned Data", `environment.TNCDataEntityId`) is the
   * active selection. When it is, we behave as if all entities are visible.
   *
   * Matched by ID only, on purpose: the display name is owned by the DB and can be renamed,
   * so comparing against it would silently stop matching.
   */
  isTNCDataSelection(): boolean {
    if (!this.hasGlobalDataAccess()) {
      return false;
    }
    const selectedId = this.getActiveDcrId();
    if (!selectedId) {
      return false;
    }
    const normalizedSelected = this.normalizeId(selectedId);
    const normalizedTNCDataEntity = this.normalizeId(this.TNCDataEntityId);
    return !!normalizedTNCDataEntity && normalizedSelected === normalizedTNCDataEntity;
  }

  /**
   * THE rule for "does the sidebar entity selector filter this list, and by what".
   * Every entity-scoped list must go through this, so the selector behaves the same way on every
   * page instead of page by page.
   *
   * Returns the normalized entity id to filter by, or `null` meaning "no filter, show everything":
   *  - Global Data Scope (global-access user, no entity selected) -> null
   *  - any selected entity -> its id, lowercased and trimmed
   *
   * `_TNC Owned Data` is an entity like any other here: selecting it scopes the list to it. That
   * mirrors what the backend already does, since selecting an entity rewrites `entityID` in the
   * JWT and the list endpoints filter on it server-side.
   */
  getEntityScopeFilterId(): string | null {
    if (this.hasGlobalDataScope()) {
      return null;
    }
    const id = this.getActiveDcrId();
    return id ? this.normalizeId(id) : null;
  }

  /**
   * True when a record owned by `entityId` is visible under the current selection.
   * Records with no owner are treated as global and always visible.
   */
  matchesEntityScope(entityId?: string | null): boolean {
    const filterId = this.getEntityScopeFilterId();
    if (!filterId) {
      return true;
    }
    const normalized = this.normalizeId(entityId);
    return !normalized || normalized === filterId;
  }

  /**
   * Check if the currently selected DCR has access to Vision
   */
  currentDcrHasVisionAccess(): boolean {
    const user = this.currentUserValue;
    if (!user) {
      return false;
    }

    // The global "_TNC Data" selection is not a real per-DCR context: Vision
    // works per-DCR, so despite its hardcoded hasVisionAccess flag it must be
    // treated as no access (otherwise Vision pages hang with no DCR context).
    if (this.isTNCDataSelection()) {
      return false;
    }

    const activeDcrId = this.getActiveDcrId();
    if (!activeDcrId) {
      return false;
    }

    const normalized = this.normalizeId(activeDcrId);
    const dcr = user.availableDCRs?.find(d => {
      if (!d) {
        return false;
      }
      return this.normalizeId(d.id) === normalized;
    });
    return dcr?.hasVisionAccess ?? false;
  }

  getFirstPartyGroupId(): string | null {
    return this.currentUserValue?.firstPartyGroupID ?? null;
  }

  getAudienceCreationDcrId(): string | null {
    const user = this.currentUserValue;
    // Always use the currently active DCR for audience creation
    const active = this.getActiveDcrId();
    if (active) {
      return active;
    }

    // If no DCR is selected, use the user's default entity
    if (user?.entityID) {
      return user.entityID;
    }

    // If still no DCR, use the first available DCR
    if (user?.availableDCRs && user.availableDCRs.length > 0) {
      return user.availableDCRs[0].id;
    }

    return null;
  }

  getActiveDcr(): string | null {
    return this.selectedDcrSubject.value;
  }

  getActiveDcrId(): string | null {
    const selected = this.selectedDcrSubject.value;
    if (selected && selected.trim().length > 0) {
      return selected;
    }
    return this.currentUserSubject.value?.entityID ?? null;
  }

  /**
   * Resolve a human readable name for the given DCR / entity id.
   */
  getDcrNameById(dcrId?: string | null): string | null {
    const normalized = (dcrId ?? '').toString().trim();
    if (!normalized) {
      return null;
    }
    const user = this.currentUserSubject.value;
    const available = user?.availableDCRs ?? [];
    const match = available.find(entry => entry && entry.id === normalized);
    const matchLabel = this.extractDcrLabel(match);
    return matchLabel ?? normalized;
  }

  private extractDcrLabel(entry?: Partial<UserDcr> | null): string | null {
    if (!entry) {
      return null;
    }
    const candidates = [
      entry.Name,
      entry.name,
      (entry as any).label,
      (entry as any).Label,
      (entry as any).title,
      (entry as any).displayName
    ];
    for (const value of candidates) {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }
    if (entry.id) {
      const idValue = String(entry.id).trim();
      if (idValue.length > 0) {
        return idValue;
      }
    }
    return null;
  }




  public getDefaultTncApiBaseUrl(): string {
    return environment.apiUrl;
  }

  public removeDcr(publisherId: string): void {
    const user = this.currentUserValue;
    if (!user) return;
    const filtered = (user.availableDCRs ?? []).filter(d => d.id !== publisherId);
    this.currentUserSubject.next({ ...user, availableDCRs: filtered });
  }

  public upsertDcr(publisher: any): void {
    const dcr = this.mapPublisherToDcr(publisher);
    if (!dcr) return;
    const user = this.currentUserValue;
    if (!user) return;
    const current = user.availableDCRs ?? [];
    // mergeDcrLists: primary wins on conflict — new dcr takes precedence for name/data updates
    const merged = this.mergeDcrLists([dcr], current);
    const sorted = merged.sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? '', undefined, { sensitivity: 'base' }));
    this.currentUserSubject.next({ ...user, availableDCRs: sorted });
  }

  private mapPublisherToDcr(publisher: any): UserDcr | null {
    if (!publisher) {
      return null;
    }

    const normalize = (value: any): string => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    const id = normalize(publisher.ID ?? publisher.id ?? publisher.PublisherID ?? publisher.publisherId);
    if (!id) {
      return null;
    }

    const name = normalize(publisher.Name ?? publisher.name ?? publisher.Label ?? publisher.label ?? publisher.title) || id;
    const rawVisionAccess = publisher.hasVisionAccess ?? publisher.HasVisionAccess ?? publisher.visionAccess ?? publisher.VisionAccess;
    const hasVisionAccess = typeof rawVisionAccess === 'boolean'
      ? rawVisionAccess
      : rawVisionAccess === 1 || rawVisionAccess === '1';
    const rawSandbox = publisher.isSandbox ?? publisher.IsSandbox;
    const isSandbox = typeof rawSandbox === 'boolean'
      ? rawSandbox
      : rawSandbox === 1 || rawSandbox === '1';
    const firstPartyGroupID = normalize(publisher.FirstPartyGroupID ?? publisher.firstPartyGroupID ?? '') || null;
    return { id, Name: name, name, hasVisionAccess, isSandbox, firstPartyGroupID };
  }

  private mergeDcrLists(primary: UserDcr[], secondary: UserDcr[]): UserDcr[] {
    const merged = new Map<string, UserDcr>();
    const normalize = (value: any): string => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };
    const hasMeaningfulName = (value: string | undefined, id: string): boolean => {
      if (!value) {
        return false;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        return false;
      }
      return trimmed.toLowerCase() !== id.toLowerCase();
    };
    const register = (item: UserDcr | null | undefined, preferExisting: boolean) => {
      if (!item) return;
      const id = normalize(item.id);
      if (!id) return;
      const normalizedName = normalize(item.Name) || normalize(item.name) || id;
      const existing = merged.get(id);
      if (!existing) {
        merged.set(id, {
          id,
          Name: normalizedName || id,
          name: normalizedName || id,
          hasVisionAccess: item.hasVisionAccess,
          isSandbox: item.isSandbox,
          firstPartyGroupID: item.firstPartyGroupID
        });
        return;
      }
      const canOverrideName = !preferExisting || !hasMeaningfulName(existing.Name, id);
      if (hasMeaningfulName(normalizedName, id) && canOverrideName) {
        existing.Name = normalizedName;
        existing.name = normalizedName;
      }
      if (item.hasVisionAccess !== undefined) {
        if (!preferExisting || existing.hasVisionAccess === undefined) {
          existing.hasVisionAccess = item.hasVisionAccess;
        }
      }
      if (item.isSandbox !== undefined) {
        if (!preferExisting || existing.isSandbox === undefined) {
          existing.isSandbox = item.isSandbox;
        }
      }
      // Never let a source that doesn't know the first-party group erase one we already have:
      // only a non-empty value can overwrite, and only if it isn't the lower-priority source.
      if (item.firstPartyGroupID) {
        if (!preferExisting || !existing.firstPartyGroupID) {
          existing.firstPartyGroupID = item.firstPartyGroupID;
        }
      }
    };

    (primary || []).forEach(item => register(item, false));
    (secondary || []).forEach(item => register(item, true));

    return Array.from(merged.values());
  }

  /**
   * Get authorization header for HTTP requests
   */
  getAuthHeaders(): { [key: string]: string } {
    const token = this.getStoredToken();
    if (!token) return {};

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get configured TNC API base URL
   */
  getTncApiBaseUrl(): string {
    return environment.apiUrl;
  }

  private getAuthApiUrl(): string {
    const resolvedBase = (environment.apiUrl || '').replace(/\/+$/u, '');
    return `${resolvedBase}/webapp`;
  }

  /**
   * Get stored TNC API token (used for direct api.tncid.app calls)
   */
  getTncApiToken(): string | null {
    const token = this.currentUserSubject.value?.tncApiToken;
    if (token && token.trim().length > 0) {
      return token.trim();
    }
    return this.getStoredTncApiToken();
  }

  clearTncApiToken(): void {
    this.storeTncApiToken(null);
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(token: string, user: SessionUser): void {
    if (token) {
      localStorage.setItem('auth_token', token);
    }
    this.storeTncApiToken(user?.tncApiToken ?? null);
    this.updateStoredUserData(user);
  }

  /**
   * Update stored user data without touching the token
   */
  private updateStoredUserData(user: SessionUser): void {
    const { availableDCRs: _, ...userToStore } = user as any;
    localStorage.setItem('user_data', JSON.stringify(userToStore));
  }

  private storeTncApiToken(token: string | null): void {
    if (token && token.trim().length > 0) {
      localStorage.setItem('tnc_api_token', token);
    } else {
      localStorage.removeItem('tnc_api_token');
    }
  }

  private getStoredTncApiToken(): string | null {
    const token = localStorage.getItem('tnc_api_token');
    if (!token) {
      return null;
    }
    const trimmed = token.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  /**
   * Clear all localStorage on logout, restoring only user preferences.
   * snapshot+clear+restore also removes orphaned keys from old app versions.
   */
  private clearAuthData(): void {
    const PERSISTENT_KEYS = ['language'];
    const snapshot: Record<string, string> = {};
    for (const key of PERSISTENT_KEYS) {
      const value = localStorage.getItem(key);
      if (value !== null) {
        snapshot[key] = value;
      }
    }
    localStorage.clear();
    for (const [key, value] of Object.entries(snapshot)) {
      localStorage.setItem(key, value);
    }
    sessionStorage.clear();
  }

  /**
   * Get stored token from localStorage
   */
  private getStoredToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Get stored user data from localStorage
   */
  private getStoredUserData(): SessionUser | null {
    const userData = localStorage.getItem('user_data');
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  /**
   * Normalize raw API or stored data into a consistent SessionUser object
   */
  private normalizeUserResponse(data: any): SessionUser {
    if (!data) {
      throw new Error('Invalid user data');
    }

    const availableRaw = data.availableDCRs ?? data.accessibleDCRs ?? [];
    const hasRealDcrList = Array.isArray(availableRaw) && availableRaw.length > 0;
    let availableDcrs: UserDcr[] = [];

    if (Array.isArray(availableRaw)) {
      const mapped: UserDcr[] = [];
      for (const entry of availableRaw) {
        if (entry === null || entry === undefined) {
          continue;
        }
        if (typeof entry === 'string') {
          const trimmed = entry.trim();
          if (trimmed) {
            mapped.push({ id: trimmed, Name: trimmed, name: trimmed });
          }
          continue;
        }
        if (typeof entry === 'object') {
          const rawId = entry.id ?? entry.ID ?? entry.DCR_ID ?? entry.dcrId ?? entry.value;
          const rawName = entry.name ?? entry.Name ?? entry.label ?? entry.Label ?? entry.title;
          if (!rawId) {
            continue;
          }
          const id = String(rawId).trim();
          if (!id) {
            continue;
          }
          const name = rawName ? String(rawName).trim() : id;
          const normalizedName = name || id;
          const hasVisionAccess = entry.hasVisionAccess ?? entry.HasVisionAccess ?? entry.visionAccess ?? false;
          const rawSandbox = entry.isSandbox ?? entry.IsSandbox;
          const isSandbox = typeof rawSandbox === 'boolean' ? rawSandbox : rawSandbox === 1 || rawSandbox === '1';
          // The login/refresh payload carries each entity's first-party Source Group: keep it here,
          // it is what lets Global Data Scope resolve the SourceGroupID of the selected owner.
          const firstPartyGroupID = String(entry.firstPartyGroupID ?? entry.FirstPartyGroupID ?? '').trim() || null;
          mapped.push({ id, Name: normalizedName, name: normalizedName, hasVisionAccess, isSandbox, firstPartyGroupID });
          continue;
        }
      }
      availableDcrs = mapped;
    }

    const userDcr = data.DCR_ID ? String(data.DCR_ID) : undefined;
    if (userDcr) {
      const hasUserDcr = availableDcrs.some(dcr => dcr.id === userDcr);
      if (!hasUserDcr) {
        availableDcrs.unshift({ id: userDcr, Name: userDcr, name: userDcr });
      }
    }

    let uniqueDcrs = availableDcrs.filter((dcr, index, arr) =>
      arr.findIndex(other => other.id === dcr.id) === index
    );

    // Sort alphabetically so "first in list" is deterministic
    uniqueDcrs.sort((a, b) => (a.Name ?? a.id ?? '').localeCompare(b.Name ?? b.id ?? '', undefined, { sensitivity: 'base' }));

    // If user is admin and the global data entity is not in availableDCRs, add it.
    // Safety net only: the authoritative name lives in `02_partner_data`.`02_publishers`
    // and normally reaches us from the login / available-entities payload. This literal
    // is a last-resort label for when that row is missing (e.g. Status != 1), so keep it
    // in sync with the DB name.
    const hasGlobalDataAccess = (data.dataAccessLevel ?? data.DataAccessLevel) === 'global';
    const TNCDataEntityId = environment.TNCDataEntityId;
    if (hasGlobalDataAccess && !uniqueDcrs.some(d => d.id === TNCDataEntityId)) {
      uniqueDcrs.push({
        id: TNCDataEntityId,
        Name: '_TNC Owned Data',
        name: '_TNC Owned Data',
        hasVisionAccess: false
      });
    }

    const statusValue =
      typeof data.status === 'number'
        ? data.status
        : String(data.status || '').toLowerCase() === 'active'
          ? 1
          : 0;

    const tokenExpirySeconds =
      typeof data.tokenExpiry === 'number'
        ? data.tokenExpiry
        : typeof data.expires === 'number' && data.expires > 1e12
          ? Math.floor(data.expires / 1000)
          : undefined;

    const expiresMs =
      typeof data.expires === 'number'
        ? data.expires > 1e12 ? data.expires : data.expires * 1000  // Se è già in ms (> 1e12), lascialo; altrimenti converti da secondi
        : tokenExpirySeconds
          ? tokenExpirySeconds * 1000
          : undefined;

    const permissions = this.normalizePermissions(
      data.permissions ??
      data.Permissions ??
      (typeof data.permission === 'string' ? data.permission : undefined)
    );

    const apiTokenCandidate = data.tncApiToken ?? data.TncApiToken ?? data.apiToken ?? data.tokenApi;
    const normalizedApiTokenRaw = typeof apiTokenCandidate === 'string'
      ? apiTokenCandidate.trim()
      : '';
    const normalizedApiToken = normalizedApiTokenRaw.length > 0 ? normalizedApiTokenRaw : null;

    // Priority for initial DCR selection:
    // 0. explicit null on a global-access user => Global Data Scope, a deliberate selection
    // 1. entityID (user's main entity)
    // 2. DCR_ID (legacy fallback from old stored data)
    // 3. TNCDataEntityId if user is admin with no entityID
    // 4. First available DCR
    const userEntityId = data.entityID ?? data.entityId ?? data.EntityID ?? null;

    // A *present* key holding null means the entity was explicitly cleared (setGlobalScope);
    // an absent key means "never chosen". Only the former is Global Data Scope — without this
    // distinction a page refresh would resolve the null back to _TNC Owned Data via rule 3/4.
    const entityIdExplicitlyNull =
      !userEntityId &&
      ('entityID' in data || 'entityId' in data || 'EntityID' in data);

    const effectiveDcrId = (() => {
      if (hasGlobalDataAccess && entityIdExplicitlyNull) {
        return null;
      }
      if (userEntityId) {
        // When no real DCR list was provided (e.g. loaded from localStorage where availableDCRs
        // is stripped), trust the stored entityID without validating against the list.
        if (!hasRealDcrList || uniqueDcrs.some(d => d.id === userEntityId)) {
          return userEntityId;
        }
      }
      // Fallback to DCR_ID if exists
      if (userDcr && uniqueDcrs.some(d => d.id === userDcr)) {
        return userDcr;
      }
      // If admin with no entityID, use TNCDataEntityId from environment
      if (hasGlobalDataAccess && !userEntityId) {
        const TNCData = uniqueDcrs.find(d => d.id === TNCDataEntityId);
        if (TNCData) {
          return TNCDataEntityId;
        }
      }
      // Last resort: first available DCR
      return uniqueDcrs[0]?.id ?? null;
    })();
    const dataAccessLevel = data.dataAccessLevel ?? data.DataAccessLevel ?? undefined;
    // hasGlobalDataAccess è derivato da dataAccessLevel (vedi sopra): il backend non lo firma più
    // nel JWT, per non avere due copie dello stesso fatto che possono divergere.
    // Allow null entityID for users without DCRs - they will be redirected to Reach
    const normalized: SessionUser = {
      ID: String(data.ID),
      email: data.email,
      // Derivato da dataAccessLevel, non letto dal payload: il backend non lo firma più nel JWT
      // proprio per non avere due copie dello stesso fatto che possono divergere.
      entityID: effectiveDcrId,
      status: statusValue,
      availableDCRs: uniqueDcrs,
      dataAccessLevel,
      name: data.name ?? data.Name ?? undefined,
      permissions,
      token: data.token ?? data.Token ?? undefined,
      tokenExpiry: tokenExpirySeconds,
      expires: expiresMs,
      tncApiToken: normalizedApiToken,
      firstPartyGroupID: data.firstPartyGroupID ?? data.FirstPartyGroupID ?? null,
      experimentalFeatures: this.normalizeExperimentalFeatures(
        data.experimentalFeatures ?? data.ExperimentalFeatures
      )
    };

    return normalized;
  }

  /**
   * Navigate to login page
   */
  redirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  /**
   * Navigate to public dashboard
   */
  redirectToPublic(): void {
    this.router.navigate(['/public']);
  }

  private userHasPermission(user: SessionUser | null | undefined, permission: string): boolean {
    if (!user) {
      return false;
    }
    // Feature access is governed by permissions only. hasGlobalDataAccess is a data-ownership flag
    // (see hasGlobalDataScope) and must NOT grant features — an admin without a permission
    // does not have that feature. 'all' in the permissions array still implies everything.
    const normalizedPermission = (permission ?? '').toString().trim().toLowerCase();
    if (!normalizedPermission) {
      return true;
    }
    return checkTreePermission(user.permissions, normalizedPermission);
  }

  /**
   * The backend only sends enabled flags, but stored sessions written by older builds may carry
   * explicit `false` entries — keep only what is truthy so `{ x: false }` and `{}` behave alike.
   */
  private normalizeExperimentalFeatures(raw: any): Record<string, boolean> {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return {};
    }
    const state: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value) {
        state[key] = true;
      }
    }
    return state;
  }

  private normalizePermissions(raw: any): string[] {
    const mapValues = (values: any[]): string[] =>
      values
        .map(value => {
          if (typeof value === 'string') {
            return value;
          }
          if (value instanceof ArrayBuffer) {
            return String.fromCharCode.apply(null, Array.from(new Uint8Array(value)));
          }
          if (value !== null && value !== undefined) {
            return String(value);
          }
          return '';
        })
        .map(value => value.trim().toLowerCase())
        .filter(value => value.length > 0);

    if (Array.isArray(raw)) {
      return mapValues(raw);
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) {
        return [];
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return mapValues(parsed);
        }
      } catch {
        // Not valid JSON; ignore and fallback to comma-separated string
      }
      return mapValues(trimmed.split(','));
    }

    if (raw && typeof raw === 'object') {
      return mapValues(Object.values(raw));
    }

    return [];
  }

  private normalizeId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }
}
